import {
  SubtaskDefaultFieldId,
  TaskDefaultFieldId,
} from "@jugl-web/domain-resources/tasks";
import { useTranslations } from "@jugl-web/utils";
import { ReactNode, useCallback, useMemo } from "react";
import { useTaskObjectNaming } from "../useTaskObjectNaming";
import { ReactComponent as ActivityIcon } from "./assets/activity.svg";
import { ReactComponent as AssigneeIcon } from "./assets/assignee.svg";
import { ReactComponent as BoardIcon } from "./assets/board.svg";
import { ReactComponent as ChecklistIcon } from "./assets/checklist.svg";
import { ReactComponent as CustomerIcon } from "./assets/customer.svg";
import { ReactComponent as DueDateIcon } from "./assets/due-date.svg";
import { ReactComponent as LabelIcon } from "./assets/label.svg";
import { ReactComponent as PriorityIcon } from "./assets/priority.svg";
import { ReactComponent as RecurringIcon } from "./assets/recurring.svg";
import { ReactComponent as StatusIcon } from "./assets/status.svg";
import { ReactComponent as TaskNameIcon } from "./assets/task-name.svg";
import { ReactComponent as TimeSpentIcon } from "./assets/time-spent.svg";
import { ReactComponent as TimeTrackedIcon } from "./assets/time-tracked-g.svg";

export interface TaskDefaultField<TId extends string = string> {
  id: TId;
  title: string;
  icon: ReactNode;
}

export const useTaskDefaultFields = ({
  entityId,
  boardId,
}: {
  entityId: string;
  boardId: string | null;
}) => {
  const taskObjectNaming = useTaskObjectNaming({
    entityId,
    boardId,
  });

  const { t } = useTranslations();

  const defaultFieldsMap = useMemo<
    Record<TaskDefaultFieldId, TaskDefaultField<TaskDefaultFieldId>>
  >(
    () => ({
      task_name: {
        id: "task_name",
        title: t(
          {
            id: "tasks-page.task-name-task-default-field",
            defaultMessage: "{singularTaskObjectName} Name",
          },
          {
            singularTaskObjectName: taskObjectNaming.singular,
          }
        ),
        icon: <TaskNameIcon />,
      },
      board: {
        id: "board",
        title: t({
          id: "tasks-page.board-task-default-field",
          defaultMessage: "Board",
        }),
        icon: <BoardIcon />,
      },
      status: {
        id: "status",
        title: t({
          id: "tasks-page.status-task-default-field",
          defaultMessage: "Status",
        }),
        icon: <StatusIcon />,
      },
      assignee: {
        id: "assignee",
        title: t({
          id: "tasks-page.assignee-task-default-field",
          defaultMessage: "Assignee",
        }),
        icon: <AssigneeIcon />,
      },
      priority: {
        id: "priority",
        title: t({
          id: "tasks-page.priority-task-default-field",
          defaultMessage: "Priority",
        }),
        icon: <PriorityIcon />,
      },
      due_date: {
        id: "due_date",
        title: t({
          id: "tasks-page.due-date-task-default-field",
          defaultMessage: "Due Date",
        }),
        icon: <DueDateIcon />,
      },
      created_at: {
        id: "created_at",
        title: t({
          id: "tasks-page.created-at-task-default-field",
          defaultMessage: "Created At",
        }),
        icon: <DueDateIcon />,
      },
      updated_at: {
        id: "updated_at",
        title: t({
          id: "tasks-page.updated-at-task-default-field",
          defaultMessage: "Updated At",
        }),
        icon: <DueDateIcon />,
      },
      label: {
        id: "label",
        title: t({
          id: "tasks-page.label-task-default-field",
          defaultMessage: "Label",
        }),
        icon: <LabelIcon />,
      },
      customer: {
        id: "customer",
        title: t({
          id: "tasks-page.customer-task-default-field",
          defaultMessage: "Customer",
        }),
        icon: <CustomerIcon />,
      },
      activity: {
        id: "activity",
        title: t(
          {
            id: "tasks-page.activity-task-default-field",
            defaultMessage: "{singularTaskObjectName} Activity",
          },
          {
            singularTaskObjectName: taskObjectNaming.singular,
          }
        ),
        icon: <ActivityIcon />,
      },
      checklist: {
        id: "checklist",
        title: t({
          id: "tasks-page.subtasks-task-default-field",
          defaultMessage: "Subtasks",
        }),
        icon: <ChecklistIcon />,
      },
      time_spent: {
        id: "time_spent",
        title: t(
          {
            id: "tasks-page.time-spent-on-task",
            defaultMessage: "Time spent on {singularTaskObjectName}",
          },
          { singularTaskObjectName: taskObjectNaming.singular }
        ),
        icon: <TimeSpentIcon />,
      },
      time_tracked: {
        id: "time_tracked",
        title: t({
          id: "tasks-page.time-tracked-task-default-field",
          defaultMessage: "Time Tracked",
        }),
        icon: <TimeTrackedIcon />,
      },
      recurrence: {
        id: "recurrence",
        title: t({
          id: "tasks-page.recurrence-task-default-field",
          defaultMessage: "Recurrence",
        }),
        icon: <RecurringIcon />,
      },
    }),
    [t, taskObjectNaming.singular]
  );

  const defaultSubtaskFieldsMap = useMemo<
    Record<SubtaskDefaultFieldId, TaskDefaultField<SubtaskDefaultFieldId>>
  >(
    () => ({
      subtask_name: {
        id: "subtask_name",
        title: t({
          id: "tasks-page.subtask-name-task-default-field",
          defaultMessage: "Subtask Name",
        }),
        icon: <TaskNameIcon />,
      },
      subtask_due_date: {
        id: "subtask_due_date",
        title: t({
          id: "tasks-page.due-date-task-default-field",
          defaultMessage: "Due Date",
        }),
        icon: <DueDateIcon />,
      },
      subtask_completion: {
        id: "subtask_completion",
        title: t({
          id: "tasks-page.completion-task-default-field",
          defaultMessage: "Completion",
        }),
        icon: <ChecklistIcon />,
      },
      subtask_assignee: {
        id: "subtask_assignee",
        title: t({
          id: "tasks-page.assignee-task-default-field",
          defaultMessage: "Assignee",
        }),
        icon: <AssigneeIcon />,
      },
      subtask_activity: {
        id: "subtask_activity",
        title: t({
          id: "tasks-page.activity-subtask-default-field",
          defaultMessage: "Subtask Activity",
        }),
        icon: <ActivityIcon />,
      },
      subtask_attachments_count: {
        id: "subtask_attachments_count",
        title: t({
          id: "common.attachments",
          defaultMessage: "Attachments",
        }),
        icon: <ActivityIcon />,
      },
    }),
    [t]
  );

  const getDefaultFieldById = useCallback(
    (fieldId: TaskDefaultFieldId) => defaultFieldsMap[fieldId],
    [defaultFieldsMap]
  );

  const getDefaultFields = useCallback(
    () => Object.values(defaultFieldsMap),
    [defaultFieldsMap]
  );

  return {
    defaultFieldsMap,
    defaultSubtaskFieldsMap,
    getDefaultFieldById,
    getDefaultFields,
  };
};
